import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Maximize, Pause, Play, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { entrarEmTelaCheia, manterTelaAcesa, sairDaTelaCheia } from "@/lib/telaCheia";

export const DURACAO_SLIDE_MS = 20_000;

export interface SlideTv {
  id: string;
  /** Sobretítulo do header (ex. "Silpi Farma"). */
  secao: string;
  titulo: string;
  conteudo: ReactNode;
  /** Coluna estreita à direita do conteúdo, só neste slide. */
  lateral?: ReactNode;
}

/** O que o rodapé precisa de cada consulta do painel (subconjunto do UseQueryResult). */
export interface ConsultaTv {
  dataUpdatedAt: number;
  isError: boolean;
}

interface PainelTvProps {
  slides: SlideTv[];
  /** Texto pequeno sob o relógio (ex. o período). */
  subtitulo?: string;
  consultas: ConsultaTv[];
  /** Recebe o slide em exibição (ex. para voltar à mesma tela fora da TV). */
  aoSair: (slide: SlideTv | undefined) => void;
  /** Slide em que o rodízio começa. */
  indiceInicial?: number;
}

const hora = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" });

const botaoControle = "rounded-lg bg-white/10 p-3 hover:bg-white/20";

/**
 * Casco dos modos TV: sem menu/top bar (a rota fica fora do AppLayout), tema escuro,
 * slides em rodízio, relógio e rodapé com há quanto tempo os dados foram atualizados.
 * Erro de rede não vira toast (as consultas usam meta.silent): mantém o último dado
 * e o rodapé avisa. Esc (ou sair da tela cheia) chama aoSair.
 *
 * Navegação: ← / → trocam de slide, espaço pausa/retoma; os mesmos controles (e os
 * indicadores, clicáveis) aparecem no rodapé com o mouse sobre a tela. Trocar de
 * slide reinicia a contagem; pausar congela o tempo que falta.
 */
export function PainelTv({ slides, subtitulo, consultas, aoSair, indiceInicial = 0 }: PainelTvProps) {
  const [indice, setIndice] = useState(indiceInicial);
  const [pausado, setPausado] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());
  const total = slides.length;
  const atual = slides[indice % total];

  // Tempo que falta no slide atual; sobrevive a pausas, zera a cada troca.
  const restante = useRef(DURACAO_SLIDE_MS);
  // Lido no sair(), sem recriar os listeners de teclado a cada troca de slide.
  const slideAtual = useRef(atual);
  useEffect(() => {
    slideAtual.current = atual;
  }, [atual]);

  const irPara = useCallback((i: number) => setIndice(((i % total) + total) % total), [total]);
  const avancar = useCallback((passo: number) => setIndice((s) => (((s + passo) % total) + total) % total), [total]);

  const sair = useCallback(() => {
    void sairDaTelaCheia();
    aoSair(slideAtual.current);
  }, [aoSair]);

  useEffect(() => {
    restante.current = DURACAO_SLIDE_MS;
  }, [indice]);

  useEffect(() => {
    if (pausado) return;
    const inicio = Date.now();
    const timer = window.setTimeout(() => avancar(1), restante.current);
    return () => {
      window.clearTimeout(timer);
      restante.current -= Date.now() - inicio;
    };
  }, [indice, pausado, avancar]);

  useEffect(() => {
    const relogio = window.setInterval(() => setAgora(Date.now()), 30_000);
    return () => window.clearInterval(relogio);
  }, []);

  useEffect(() => manterTelaAcesa(), []);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") sair();
      else if (e.key === "ArrowRight") avancar(1);
      else if (e.key === "ArrowLeft") avancar(-1);
      else if (e.key === " ") setPausado((p) => !p);
      else return;
      // Espaço com foco num botão também o "clicaria".
      e.preventDefault();
    };
    // Em tela cheia o Esc é consumido pelo navegador para sair dela: trata pelo evento.
    const aoMudarTelaCheia = () => {
      if (!document.fullscreenElement) sair();
    };
    window.addEventListener("keydown", aoTeclar);
    document.addEventListener("fullscreenchange", aoMudarTelaCheia);
    return () => {
      window.removeEventListener("keydown", aoTeclar);
      document.removeEventListener("fullscreenchange", aoMudarTelaCheia);
    };
  }, [sair, avancar]);

  const atualizadoEm = Math.min(...consultas.map((c) => c.dataUpdatedAt || Infinity));
  const minutosDesde = Number.isFinite(atualizadoEm) ? Math.floor((agora - atualizadoEm) / 60_000) : null;
  const comFalha = consultas.some((c) => c.isError);

  if (!atual) return null;

  return (
    <div className="group fixed inset-0 flex flex-col bg-slate-950 font-sans text-slate-50">
      <header className="flex items-center justify-between px-12 pb-6 pt-10">
        <div>
          <p className="text-2xl font-medium text-teal-400">{atual.secao}</p>
          <h1 className="text-5xl font-semibold">{atual.titulo}</h1>
        </div>
        <div className="text-right">
          <p className="text-5xl font-semibold tabular-nums">{hora.format(agora)}</p>
          {subtitulo && <p className="text-xl text-slate-400">{subtitulo}</p>}
        </div>
      </header>

      <main className="relative flex min-h-0 flex-1 gap-10 px-12">
        <div className="relative min-w-0 flex-1">{atual.conteudo}</div>
        {atual.lateral && <aside className="flex w-80 shrink-0 flex-col border-l border-white/10 pl-8">{atual.lateral}</aside>}
      </main>

      <footer className="flex items-center justify-between gap-8 px-12 pb-8 pt-6">
        {/* Controles (aqui e à direita) só aparecem com o mouse sobre a tela. */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <button type="button" onClick={() => avancar(-1)} className={botaoControle} aria-label="Slide anterior">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setPausado((p) => !p)}
              className={botaoControle}
              aria-label={pausado ? "Retomar rodízio" : "Pausar rodízio"}
            >
              {pausado ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
            </button>
            <button type="button" onClick={() => avancar(1)} className={botaoControle} aria-label="Próximo slide">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center" aria-label="Slides">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => irPara(i)}
                className="px-1.5 py-2"
                aria-label={`${s.secao}: ${s.titulo}`}
                aria-current={i === indice % total ? "true" : undefined}
              >
                <span
                  className={cn(
                    "block h-3 rounded-full transition-all",
                    i === indice % total ? "w-10 bg-teal-400" : "w-3 bg-white/20 hover:bg-white/40",
                  )}
                />
              </button>
            ))}
          </div>
        </div>
        <BarraProgresso chave={indice} pausado={pausado} />
        {pausado && <p className="text-lg font-medium text-teal-400">Pausado</p>}
        <p className={cn("text-lg", comFalha ? "text-amber-400" : "text-slate-500")}>
          {minutosDesde === null
            ? "Carregando…"
            : `${comFalha ? "Sem conexão · " : ""}Atualizado ${minutosDesde < 1 ? "agora" : `há ${minutosDesde} min`}`}
        </p>
        <div className="flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <button type="button" onClick={() => void entrarEmTelaCheia()} className={botaoControle} aria-label="Tela cheia">
            <Maximize className="h-5 w-5" />
          </button>
          <button type="button" onClick={sair} className={botaoControle} aria-label="Sair do modo TV">
            <X className="h-5 w-5" />
          </button>
        </div>
      </footer>
    </div>
  );
}

/** Enche em DURACAO_SLIDE_MS, recomeça a cada troca de slide e congela na pausa. */
function BarraProgresso({ chave, pausado }: { chave: number; pausado: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const animacao = useRef<Animation | undefined>(undefined);

  useEffect(() => {
    animacao.current = ref.current?.animate?.([{ width: "0%" }, { width: "100%" }], {
      duration: DURACAO_SLIDE_MS,
      easing: "linear",
      fill: "forwards",
    });
    return () => animacao.current?.cancel();
  }, [chave]);

  // Declarado depois: na troca de slide já pega a animação nova (e a pausa, se for o caso).
  useEffect(() => {
    if (pausado) animacao.current?.pause();
    else animacao.current?.play();
  }, [pausado, chave]);

  return (
    <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
      <div ref={ref} className="h-full w-0 bg-teal-400/70" />
    </div>
  );
}
